import { z } from 'zod';

import { paginated } from '../../api/pagination';

/**
 * Staff, as the roster API speaks it.
 *
 * TWO ASYMMETRIES ON THE WIRE, both deliberate and both easy to get backwards:
 *
 *  1. **Responses are snake_case, request bodies are camelCase.** The server's
 *     serializer emits `whatsapp_display`/`auto_bands`, while its zod request
 *     schemas read `hoursMode`/`autoBands`. So `staffSchema` transforms and the
 *     input schemas do not. Check `apps/api/src/features/staff/` before assuming
 *     either half — this is a contract, not a convention we can tidy.
 *  2. **`whatsapp` means two different things.** On the way out it is E.164
 *     (`+61412345678`) with `whatsappDisplay` alongside it; on the way in it is
 *     whatever the coordinator typed, and the SERVER normalises. Never send
 *     `whatsappDisplay` back as `whatsapp` — it round-trips today and would
 *     break the moment the display format changes.
 *
 * The enums are strict on purpose. A job the client has never heard of should
 * fail the parse loudly rather than render as an empty chip, because the value
 * is also what the form posts back — a silent passthrough would write rubbish.
 */

export const JOBS = [
  'bowl_clean',
  'unspecified',
  'day_clean',
  'event_clean',
  'supervisor',
  'post_clean',
] as const;
export type Job = (typeof JOBS)[number];

export const GENDERS = ['female', 'male', 'other'] as const;
export type Gender = (typeof GENDERS)[number];

/** Time-of-day buckets a shift start falls into. */
export const BANDS = ['early', 'day', 'evening', 'night'] as const;
export type Band = (typeof BANDS)[number];

/**
 * How eagerly someone is offered a matching shift.
 *
 * Ordered least to most, because the picker turns it into a sort key. It never
 * SELECTS anyone — it only orders, which is what the "nobody is selected for
 * you" hint promises.
 */
export const AUTO_LEVELS = ['none', 'usual', 'priority'] as const;
export type AutoLevel = (typeof AUTO_LEVELS)[number];

/** `fn` means a fortnightly cap applies; `unlimited` ignores `fortnightHours`. */
export const HOURS_MODES = ['unlimited', 'fn'] as const;
export type HoursMode = (typeof HOURS_MODES)[number];

export const staffSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    job: z.enum(JOBS),
    whatsapp: z.string(),
    whatsapp_display: z.string(),
    city: z.string(),
    gender: z.enum(GENDERS).nullable(),
    hours_mode: z.enum(HOURS_MODES),
    fortnight_hours: z.number().int().nullable(),
    availability: z.string(),
    auto_level: z.enum(AUTO_LEVELS),
    auto_bands: z.array(z.enum(BANDS)),
    /** An instant, not a formatted date — the client owns en-AU. */
    archived_at: z.iso.datetime().nullable(),
    /**
     * Set when they replied STOP: they are not messaged while it is.
     * Optional so an app build talking to an older API still parses.
     */
    opted_out_at: z.iso.datetime().nullable().optional(),
    created_at: z.iso.datetime(),
  })
  .transform((raw) => ({
    id: raw.id,
    name: raw.name,
    job: raw.job,
    /** E.164. What the server stores and what uniqueness is checked against. */
    whatsapp: raw.whatsapp,
    /** `0412 880 143`. Display only — never post this back. */
    whatsappDisplay: raw.whatsapp_display,
    city: raw.city,
    gender: raw.gender,
    hoursMode: raw.hours_mode,
    fortnightHours: raw.fortnight_hours,
    availability: raw.availability,
    autoLevel: raw.auto_level,
    autoBands: raw.auto_bands,
    archivedAt: raw.archived_at,
    optedOutAt: raw.opted_out_at ?? null,
    createdAt: raw.created_at,
  }));

export type Staff = z.infer<typeof staffSchema>;

/**
 * `archived_at` is the ONLY thing that marks someone as ex-staff.
 *
 * There is no separate collection, which is the difference from the local store
 * this replaced: one row moves between two views. Exported as a predicate so no
 * screen re-derives it as `!== null` and gets the sense backwards.
 */
export function isArchived(person: Staff): boolean {
  return person.archivedAt !== null;
}

export const staffPageSchema = paginated(staffSchema);
export type StaffPage = z.infer<typeof staffPageSchema>;

export const staffListParamsSchema = z.object({
  /**
   * `active` by default, matching the server. The Staffs screen and the Ex
   * Staffs screen are the same endpoint with this flipped.
   */
  status: z.enum(['active', 'archived', 'all']).default('active'),
  /** Sent as `q` — see `listStaff`. Named `search` here to match the repo. */
  search: z.string().trim().optional(),
  job: z.enum(JOBS).optional(),
  band: z.enum(BANDS).optional(),
});

export type StaffListParamsInput = z.input<typeof staffListParamsSchema>;
/** Defaults resolved. Query keys use this so they are stable. */
export type StaffListParams = z.infer<typeof staffListParamsSchema>;

/**
 * Australian and Nepali mobiles, as the form accepts them.
 *
 * Deliberately generous, and deliberately DUPLICATED from the server's
 * `normaliseMobile`. This one is a form validator sitting in front of a
 * controlled input; that one parses an untrusted request body. Same behaviour,
 * two implementations, on purpose — see the boundary note in AGENTS.md.
 */
// The app's phone fields send E.164 (`+61452136787`, `+9779812345678`); older
// callers send the nine digits after a fixed +61, `0452…` or `+61 452…`.
const MOBILE =
  /^(?:(?:\+?61\s?|0)?4\d{2}\s?\d{3}\s?\d{3}|(?:\+?977[\s-]?)?9[678]\d[\s-]?\d{3}[\s-]?\d{4})$/;

const baseStaffInputSchema = z.object({
  name: z.string().trim().min(1, 'Enter the employee’s name.').max(120),
  job: z.enum(JOBS).default('unspecified'),
  whatsapp: z.string().trim().regex(MOBILE, {
    message: 'Enter an Australian or Nepali mobile — +61 4XX XXX XXX or +977 98X XXX XXXX.',
  }),
  city: z.string().trim().max(120).default(''),
  gender: z.enum(GENDERS).nullish(),
  hoursMode: z.enum(HOURS_MODES).default('unlimited'),
  /** A number, not the form's string. 336 hours is a full fortnight. */
  fortnightHours: z.coerce.number().int().positive().max(336).nullish(),
  availability: z.string().trim().max(500).default(''),
  autoLevel: z.enum(AUTO_LEVELS).default('none'),
  autoBands: z.array(z.enum(BANDS)).default([]),
});

/**
 * The two cross-field rules, as `superRefine` with a `path`.
 *
 * Never a bare `.refine()`: the error has to land on the field the user must
 * fix, or the form marks everything equally invalid and the person hunts for
 * the problem. Both rules are also enforced server-side — the database carries
 * the hours pairing as a CHECK — because a rule enforced only in the client is
 * a rule that curl walks around.
 */
function crossFieldRules(
  value: Partial<z.infer<typeof baseStaffInputSchema>>,
  ctx: z.RefinementCtx,
): void {
  if (
    value.hoursMode === 'fn' &&
    (value.fortnightHours === null || value.fortnightHours === undefined)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['fortnightHours'],
      message: 'Enter the hours allowed per fortnight.',
    });
  }

  if (
    value.autoLevel !== undefined &&
    value.autoLevel !== 'none' &&
    (value.autoBands?.length ?? 0) === 0
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['autoBands'],
      message: 'Pick at least one shift time this applies to.',
    });
  }
}

export const createStaffInputSchema = baseStaffInputSchema.superRefine(crossFieldRules);
/** What a form binds to, before defaults are applied. */
export type CreateStaffInput = z.input<typeof createStaffInputSchema>;

/**
 * PATCH: every field optional.
 *
 * The hours pairing is only checkable here when `hoursMode` is present in the
 * body; a partial update that omits it is validated by the SERVER against the
 * stored row. That asymmetry is why the server does it too rather than
 * trusting this.
 */
export const updateStaffInputSchema = baseStaffInputSchema.partial().superRefine((value, ctx) => {
  if (value.hoursMode === undefined) return;
  crossFieldRules(value, ctx);
});

export type UpdateStaffInput = z.input<typeof updateStaffInputSchema>;

/**
 * One event on a staff member's shift history — a shift they said YES to.
 *
 * `shiftTimes` are THEIR start times on the event (`HH:mm`), not the event's:
 * one person works one shift per event, so this is usually a single time.
 */
const staffShiftSchema = z
  .object({
    event_id: z.string(),
    event_name: z.string(),
    event_type: z.string(),
    event_date: z.string(),
    shift_times: z.array(z.string()),
  })
  .transform((raw) => ({
    eventId: raw.event_id,
    eventName: raw.event_name,
    eventType: raw.event_type,
    eventDate: raw.event_date,
    shiftTimes: raw.shift_times,
  }));

export type StaffShift = z.infer<typeof staffShiftSchema>;

/**
 * `GET /api/staff/:id/shifts`: confirmed shifts, split by whether the person's
 * own shift has STARTED — the server decides, in the roster's timezone, so the
 * split cannot depend on which timezone this phone is set to. Upcoming come
 * soonest first; past, most recent first. Cancelled events never appear.
 */
export const staffShiftsSchema = z
  .object({
    staff_id: z.string(),
    upcoming: z.array(staffShiftSchema),
    past: z.array(staffShiftSchema),
  })
  .transform((raw) => ({ staffId: raw.staff_id, upcoming: raw.upcoming, past: raw.past }));

export type StaffShifts = z.infer<typeof staffShiftsSchema>;

/** One shift of an open event, from one person's side. */
const staffOpenShiftSchema = z
  .object({
    id: z.string(),
    start_time: z.string(),
    min_staff: z.number().int().positive(),
    yes_count: z.number().int().nonnegative(),
    /** This person is on it. */
    assigned: z.boolean(),
    /** It has started — nobody new can be asked for it. */
    started: z.boolean(),
  })
  .transform((raw) => ({
    id: raw.id,
    startTime: raw.start_time,
    minStaff: raw.min_staff,
    yesCount: raw.yes_count,
    assigned: raw.assigned,
    started: raw.started,
  }));

/**
 * One OPEN event, from one person's side — what their own page needs to send
 * several events to just them.
 *
 * `sendable` is the SERVER's answer to "would a send ask them anything here?":
 * an assignment neither notified nor already in a waiting message. It is the
 * same set the multi-event dispatch messages, so a tick on the page is a line
 * in their message.
 */
const staffOpenEventSchema = z
  .object({
    event_id: z.string(),
    event_name: z.string(),
    event_type: z.string(),
    event_date: z.string(),
    shifts: z.array(staffOpenShiftSchema),
    assigned: z.boolean(),
    sendable: z.boolean(),
    queued: z.boolean(),
    notified: z.boolean(),
    answer: z.enum(['yes', 'no']).nullable(),
  })
  .transform((raw) => ({
    eventId: raw.event_id,
    eventName: raw.event_name,
    eventType: raw.event_type,
    eventDate: raw.event_date,
    shifts: raw.shifts,
    assigned: raw.assigned,
    sendable: raw.sendable,
    queued: raw.queued,
    notified: raw.notified,
    answer: raw.answer,
  }));

export type StaffOpenEvent = z.infer<typeof staffOpenEventSchema>;
export type StaffOpenShift = StaffOpenEvent['shifts'][number];

/**
 * `GET /api/staff/:id/open-events`: every open event they are on or could be
 * added to, date order. Events whose shifts — theirs, or all of them — have
 * started are left out by the server, in the roster's timezone.
 */
export const staffOpenEventsSchema = z
  .object({
    staff_id: z.string(),
    events: z.array(staffOpenEventSchema),
  })
  .transform((raw) => ({ staffId: raw.staff_id, events: raw.events }));

export type StaffOpenEvents = z.infer<typeof staffOpenEventsSchema>;
